import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Box, Button, Container, TextField, Typography, InputAdornment, IconButton } from '@material-ui/core';
import { Visibility, VisibilityOff, Lock } from '@material-ui/icons';
import swal from 'sweetalert';
import Api from '../utils/api';

const ResetPassword = () => {
  const navigate = useNavigate();
  const api = new Api();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const onSubmit = async (values, actions) => {
    actions.setSubmitting(true);

    try {
      if (!token) {
        throw new Error('Token inválido ou expirado');
      }

      await api.post('/users/reset-password', {
        token,
        password: values.password
      });

      await swal({
        title: 'Senha redefinida!',
        text: 'Sua senha foi alterada com sucesso. Você já pode fazer login.',
        icon: 'success',
        button: 'Ir para Login'
      });

      navigate('/login');
    } catch (error) {
      console.error('Erro ao redefinir senha:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Erro ao redefinir senha. Por favor, tente novamente.';

      swal({
        title: 'Erro',
        text: errorMessage,
        icon: 'error'
      });
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Redefinir Senha | Sistema de Afiliados</title>
      </Helmet>
      <section
        style={{
          position: 'relative',
          minHeight: '100vh',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(255,255,255,.25), rgba(105, 80, 162, 0.5))',
            zIndex: 1
          }}
        />

        <div style={{ position: 'relative', zIndex: 2 }}>
          <Box
            sx={{
              backgroundColor: 'transparent',
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
              width: '100vw',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Container
              maxWidth="sm"
              sx={{ backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: 2, p: 4, boxShadow: 5 }}
            >
              <Formik
                initialValues={{
                  password: '',
                  confirmPassword: ''
                }}
                validationSchema={Yup.object().shape({
                  password: Yup.string().min(6, 'Senha deve ter no mínimo 6 caracteres').required('Senha é obrigatória'),
                  confirmPassword: Yup.string()
                    .oneOf([Yup.ref('password'), null], 'As senhas não coincidem')
                    .required('Confirmação de senha é obrigatória')
                })}
                onSubmit={onSubmit}
              >
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                  <form onSubmit={handleSubmit}>
                    <Box
                      sx={{
                        mb: 3,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <img
                        alt="Logo"
                        src="/static/logo.png"
                        style={{
                          width: 'auto',
                          height: 75,
                          resize: 'contain'
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography color="textPrimary" variant="h2">
                        Redefinir Senha
                      </Typography>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Digite sua nova senha
                      </Typography>
                    </Box>

                    <TextField
                      error={Boolean(touched.password && errors.password)}
                      fullWidth
                      helperText={touched.password && errors.password}
                      label="Nova senha"
                      margin="normal"
                      name="password"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type={showPassword ? 'text' : 'password'}
                      value={values.password}
                      variant="outlined"
                      InputProps={{
                        style: {
                          backgroundColor: 'white'
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)}>
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    <TextField
                      error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                      fullWidth
                      helperText={touched.confirmPassword && errors.confirmPassword}
                      label="Confirmar nova senha"
                      margin="normal"
                      name="confirmPassword"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={values.confirmPassword}
                      variant="outlined"
                      InputProps={{
                        style: {
                          backgroundColor: 'white'
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    <Box sx={{ py: 2 }}>
                      <Button
                        color="primary"
                        disabled={isSubmitting}
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        startIcon={<Lock />}
                      >
                        {isSubmitting ? 'Redefinindo...' : 'Redefinir senha'}
                      </Button>
                    </Box>
                  </form>
                )}
              </Formik>
            </Container>
          </Box>
        </div>
      </section>
    </>
  );
};

export default ResetPassword;
